import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cooperplaceApi } from 'services/api';
import { convertToBRLValue, convertToDollarValue } from 'v3/utils/formatter';
import { SERVICE_LEVEL_VALUES } from 'v3/utils/constants';
import { getPercentageFromValue } from 'v3/utils/functions';
import { percentage as percentageToValue } from 'utils/number';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import { useSnackbar } from 'v3/components/Snackbar';
import {
  inputsLoadConditionsToShow,
  normalizaFreightValues,
  shipmentTransferToShow,
} from './utils';
import { schema } from './validate';
import Text from '../Text';

const Context = createContext({
  name: 'FreightValuesContext',
  strict: false,
});

export const useFreightValues = () => useContext(Context);

export const BRL_PRECISION = 2;
export const USD_PRECISION = 4;
export const AD_VALOREM_PERCENTAGE_PRECISION = 4;

export default function FreightValuesContext({ children }) {
  const [data, setData] = useState({
    loadValueType: null,
    negotiationType: null,
    currency: null,
    cargoValue: null,
    exchangeDay: null,
    freightExchange: null,
    rateResponse: null,
  });
  const snackbar = useSnackbar();
  const [advanceMoney, setAdvanceMoney] = useState({});
  const [shippingCompany, setShippingCompany] = useState({});
  const [fareCompany, setFareCompany] = useState(0);
  const [defaultData, setDefaultData] = useState(null);
  const [goldenShippingCompany, setGoldenShippingCompany] = useState(false);
  const [advanceMoneyExternal, setAdvanceMoneyExternal] = useState({});
  const [isAttended, setIsAttended] = useState(false);
  const [balance, setBalance] = useState({});
  const [travelMargin, setTravelMargin] = useState(0);
  const { margins } = useContext(BlockedTravelContext);

  const [customerFields, setCustomerFields] = useState({});
  const [insuranceCoopercargaValue, setInsuranceCoopercargaValue] = useState({
    value: '0',
    dollarValue: '0',
    percentage: '0',
  });
  const [shipmentDiscounts, setShipmentDiscounts] = useState({
    issueRate: {
      value: '0',
      dollarValue: '0',
    },
  });
  const [customerExtraFields, setCustomerExtraFields] = useState([]);
  const [inputsExtraFields, setInputsExtraFields] = useState([]);
  const [shipmentDiscountsExtraFields, setShipmentDiscountsExtraFields] = useState([]);
  const [shipmentTransferExtraFields, setShipmentTransferExtraFields] = useState([]);
  const [pcpObservation, setPcpObservation] = useState('');
  const [negotiatorObservation, setNegotiatorObservation] = useState('');
  const [showDailyRate, setShowDailyRate] = useState(false);
  const [freightFieldsOptions, setFreightFieldsOptions] = useState([]);
  const [exchangeContract, setExchangeContract] = useState(0);
 const [errors, setErrors] = useState({});
  const [hasInitiated, setHasInitiated] = useState(false);
  const [totalKm, setTotalKm] = useState(0);

  async function fetchErpExchange() {
    try {
      const response = await cooperplaceApi.get('/getERPRate');
      const value = Number(response.data.value);
      return value;
    } catch (err) {
      return null;
    }
  }

  const dollarToUse = useMemo(() => {
    return showDailyRate ? data?.exchangeDay : data?.freightExchange;
  }, [data?.exchangeDay, data?.freightExchange, showDailyRate]);

  const erpExchangeValue = useMemo(() => {
    return exchangeContract || dollarToUse;
  }, [exchangeContract, dollarToUse]);

  const isBRLCurrency = useMemo(() => {
    return data?.currency?.value === 'BRL';
  }, [data?.currency]);

  function convertValueToCurrency(
    value,
    exchangeValue,
    valueCurrency = 'BRL',
    currencyToConvert = 'BRL',
  ) {
    if (valueCurrency === currencyToConvert) {
      return value;
    }

    if (valueCurrency === 'BRL') {
      return convertToDollarValue(value, exchangeValue, USD_PRECISION);
    }

    return convertToBRLValue(value, exchangeValue, BRL_PRECISION);
  }

  useEffect(() => {
    if (!hasInitiated) {
      return;
    }

    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';

    setCustomerFields((old) => ({
      ...old,
      takerValue: {
        ...old.takerValue,
        value: convertValueToCurrency(old?.takerValue?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.takerValue?.[prop], dollarToUse, currency, 'USD'),
      },
      tollValue: {
        ...old.tollValue,
        value: convertValueToCurrency(old?.tollValue?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.tollValue?.[prop], dollarToUse, currency, 'USD'),
      },
      dischargeValue: {
        ...old.dischargeValue,
        value: convertValueToCurrency(old?.dischargeValue?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(
          old?.dischargeValue?.[prop],
          dollarToUse,
          currency,
          'USD',
        ),
      },
      gris: {
        ...old.gris,
        value: convertValueToCurrency(old?.gris?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.gris?.[prop], dollarToUse, currency, 'USD'),
        percentage: Number(old?.gris?.percentage).toFixed(BRL_PRECISION),
      },
      adValorem: {
        ...old.adValorem,
        value: convertValueToCurrency(old?.adValorem?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.adValorem?.[prop], dollarToUse, currency, 'USD'),
        percentage: Number(old?.adValorem?.percentage).toFixed(AD_VALOREM_PERCENTAGE_PRECISION),
      },
      chargeValue: {
        ...old.chargeValue,
        value: convertValueToCurrency(old?.chargeValue?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.chargeValue?.[prop], dollarToUse, currency, 'USD'),
      },
      ferry: {
        ...old.ferry,
        value: convertValueToCurrency(old?.ferry?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.ferry?.[prop], dollarToUse, currency, 'USD'),
      },
      ICMS: {
        ...old.ICMS,
        value: convertValueToCurrency(old?.ICMS?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(old?.ICMS?.[prop], dollarToUse, currency, 'USD'),
        percentage: Number(old?.ICMS?.percentage).toFixed(BRL_PRECISION),
      },
    }));

    setInsuranceCoopercargaValue((old) => ({
      ...old,
      value: convertValueToCurrency(old?.[prop], dollarToUse, currency, 'BRL'),
      dollarValue: convertValueToCurrency(old?.[prop], dollarToUse, currency, 'USD'),
      percentage: Number(old?.percentage).toFixed(BRL_PRECISION),
    }));

    setCustomerExtraFields((old) =>
      old.map((field) => ({
        ...field,
        value: convertValueToCurrency(field?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(field?.[prop], dollarToUse, currency, 'USD'),
      })),
    );

    setInputsExtraFields((old) =>
      old.map((field) => ({
        ...field,
        value: convertValueToCurrency(field?.[prop], dollarToUse, currency, 'BRL'),
        dollarValue: convertValueToCurrency(field?.[prop], dollarToUse, currency, 'USD'),
      })),
    );
  }, [hasInitiated, dollarToUse, isBRLCurrency]);

  useEffect(() => {
    if (!hasInitiated) {
      return;
    }

    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';

    setAdvanceMoneyExternal((old) => ({
      ...old,
      value: convertValueToCurrency(old?.[prop], erpExchangeValue, currency, 'BRL'),
      dollarValue: convertValueToCurrency(old?.[prop], erpExchangeValue, currency, 'USD'),
    }));

    setAdvanceMoney((old) => ({
      ...old,
      value: convertValueToCurrency(old?.[prop], erpExchangeValue, currency, 'BRL'),
      dollarValue: convertValueToCurrency(old?.[prop], erpExchangeValue, currency, 'USD'),
    }));

    setBalance((old) => ({
      ...old,
      value: convertValueToCurrency(old?.[prop], erpExchangeValue, currency, 'BRL'),
      dollarValue: convertValueToCurrency(old?.[prop], erpExchangeValue, currency, 'USD'),
    }));

    setShipmentDiscounts((old) => ({
      ...old,
      issueRate: {
        ...old.issueRate,
        value: convertValueToCurrency(old?.issueRate?.[prop], erpExchangeValue, currency, 'BRL'),
        dollarValue: convertValueToCurrency(
          old?.issueRate?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      },
    }));

    setShipmentDiscountsExtraFields((old) =>
      old.map((field) => ({
        ...field,
        value: convertValueToCurrency(field?.[prop], erpExchangeValue, currency, 'BRL'),
        dollarValue: convertValueToCurrency(field?.[prop], erpExchangeValue, currency, 'USD'),
      })),
    );

    setShipmentTransferExtraFields((old) =>
      old.map((field) => ({
        ...field,
        value: convertValueToCurrency(field?.[prop], erpExchangeValue, currency, 'BRL'),
        dollarValue: convertValueToCurrency(field?.[prop], erpExchangeValue, currency, 'USD'),
      })),
    );
  }, [hasInitiated, erpExchangeValue, isBRLCurrency]);

  function onChangeDailyRate() {
    setShowDailyRate((old) => !old);
  }

  function onChangeCustomerValueField(field, nextValue, percentageValue) {
    if (customerFields[field]) {
      let value;
      let dollarValue;
      if (isBRLCurrency) {
        value = nextValue;
        dollarValue = convertToDollarValue(nextValue, dollarToUse, USD_PRECISION);
      } else {
        value = convertToBRLValue(nextValue, dollarToUse, BRL_PRECISION);
        dollarValue = nextValue;
      }

      setCustomerFields((old) => ({
        ...old,
        [field]: {
          ...old[field],
          value,
          dollarValue,
          percentage: percentageValue !== undefined ? percentageValue : old[field]?.percentage,
        },
      }));
    }
  }

  function onChangeCoopercargaInsuranceValue(nextValue, percentageValue) {
    let value;
    let dollarValue;
    if (isBRLCurrency) {
      value = nextValue;
      dollarValue = convertToDollarValue(nextValue, dollarToUse, USD_PRECISION);
    } else {
      value = convertToBRLValue(nextValue, dollarToUse, BRL_PRECISION);
      dollarValue = nextValue;
    }

    setInsuranceCoopercargaValue({
      value,
      dollarValue,
      percentage: Number(percentageValue || 0).toFixed(BRL_PRECISION),
    });
  }

  function onChangeCustomerActionField(field, action) {
    if (customerFields[field]) {
      setCustomerFields((old) => ({
        ...old,
        [field]: {
          ...old[field],
          action,
        },
      }));
    }
  }

  function onChangeCurrency(currency) {
    let exchangeDay = 0;
    if (currency?.value === 'BRL') {
      exchangeDay = data?.rateResponse?.buy || 0;
    } else if (currency?.value === 'USD') {
      exchangeDay = data?.rateResponse?.sell || 0;
    }

    setData((old) => ({
      ...old,
      currency,
      exchangeDay,
    }));
  }

  async function fetchFreightFieldsOptions() {
    try {
      const response = await cooperplaceApi.get('/load-values-observations');
      setFreightFieldsOptions(response.data);
      return response.data;
    } catch (error) {
      setFreightFieldsOptions([]);
      return null;
    }
  }

  function onChangeAdvanceMoney(nextValue, percentageValue) {
    let value;
    let dollarValue;
    if (isBRLCurrency) {
      value = nextValue;
      dollarValue = convertToDollarValue(nextValue, erpExchangeValue, USD_PRECISION);
    } else {
      value = convertToBRLValue(nextValue, erpExchangeValue, BRL_PRECISION);
      dollarValue = nextValue;
    }

    setAdvanceMoney({
      value,
      dollarValue,
      percentage: percentageValue !== undefined ? percentageValue : advanceMoney?.percentage,
    });
  }

  function onChangeBalance(nextValue) {
    let value;
    let dollarValue;
    if (isBRLCurrency) {
      value = nextValue;
      dollarValue = convertToDollarValue(nextValue, erpExchangeValue, USD_PRECISION);
    } else {
      value = convertToBRLValue(nextValue, erpExchangeValue, BRL_PRECISION);
      dollarValue = nextValue;
    }

    setBalance({
      value,
      dollarValue,
    });
  }

  function onChangeIssueRate(nextValue) {
    let value;
    let dollarValue;
    if (isBRLCurrency) {
      value = nextValue;
      dollarValue = convertToDollarValue(nextValue, erpExchangeValue, USD_PRECISION);
    } else {
      value = convertToBRLValue(nextValue, erpExchangeValue, BRL_PRECISION);
      dollarValue = nextValue;
    }

    setShipmentDiscounts((old) => ({
      ...old,
      issueRate: {
        value,
        dollarValue,
      },
    }));
  }

  function getTotalCustomerValue() {
    let total = 0;
    let totalDollarValue = 0;

    const customerFieldsToSum = Object.keys(customerFields);
    for (const field of customerFieldsToSum) {
      total += Number(customerFields[field]?.value || 0);
      totalDollarValue += Number(customerFields[field]?.dollarValue || 0);
    }

    if (customerFields?.tollValue?.action?.code === 'client_provides_card') {
      total -= Number(customerFields?.tollValue.value);
      totalDollarValue -= Number(customerFields?.tollValue.dollarValue);
    }

    if (customerExtraFields.length > 0) {
      for (const field of customerExtraFields) {
        total += Number(field.value);
        totalDollarValue += Number(field.dollarValue);
      }
    }

    return { total, totalDollarValue };
  }

  const totalCustomerValue = useMemo(() => {
    return getTotalCustomerValue();
  }, [
    customerFields?.takerValue,
    customerFields?.tollValue,
    customerFields?.dischargeValue,
    customerFields?.gris,
    customerFields?.adValorem,
    customerFields?.chargeValue,
    customerFields?.ferry,
    customerFields?.ICMS,
    customerExtraFields,
  ]);

  function findDefaultActionByCode(actions, key, field) {
    return actions[key]?.find((item) => item.code === field);
  }

  function findDefaultActionById(actions, key, id) {
    return actions[key]?.find((item) => item.id === id);
  }

  function setInputAction(actions, key, defaultCode, id) {
    if (id) {
      const action = findDefaultActionById(actions, key, id);
      return action;
    }

    return findDefaultActionByCode(actions, key, defaultCode);
  }

  function getDescriptionServiceLevel(shipper) {
    if (shipper?.shippingCompanies) {
      return (
        shipper?.shippingCompanies?.descriptionServiceLevel ||
        shipper?.shippingCompanies?.description_service_level
      );
    }

    if (shipper?.shippingCompany) {
      return (
        shipper?.shippingCompany?.descriptionServiceLevel ||
        shipper?.shippingCompany?.description_service_level
      );
    }

    return shipper?.descriptionServiceLevel || shipper?.description_service_level;
  }

  function isGoldenShippingCompany(shipper) {
    const description = getDescriptionServiceLevel(shipper);
    return description?.toLowerCase() === 'agregado ouro';
  }

  function setIssueRateValue(isGolden) {
    let issueRateValue = SERVICE_LEVEL_VALUES.default;
    let issueRateDollarValue = convertToDollarValue(
      issueRateValue,
      erpExchangeValue,
      USD_PRECISION,
    );
    if (isGolden) {
      issueRateValue = SERVICE_LEVEL_VALUES.OURO;
      issueRateDollarValue = convertToDollarValue(issueRateValue, erpExchangeValue, USD_PRECISION);
    }

    onChangeIssueRate(isBRLCurrency ? issueRateValue : issueRateDollarValue);
  }

  useEffect(() => {
    const isGolden = isGoldenShippingCompany(shippingCompany);
    setIssueRateValue(isGolden);
  }, [shippingCompany, dollarToUse, isBRLCurrency]);

  function setInsuranceValueBenner(insuranceBenner) {
    if (insuranceBenner !== undefined && insuranceBenner !== null) {
      const percentageValue = (Number(insuranceBenner) / data?.cargoValue) * 100;

      const insuranceValues = {
        value: insuranceBenner.toFixed(BRL_PRECISION),
        dollarValue: convertToDollarValue(insuranceBenner, dollarToUse, USD_PRECISION),
        percentage: percentageValue.toFixed(BRL_PRECISION),
      };

      setInsuranceCoopercargaValue(insuranceValues);
      return insuranceValues;
    }

    return insuranceCoopercargaValue;
  }

  async function onChangeShippingCompany(shipper) {
    const isGolden = isGoldenShippingCompany(shipper);
    setGoldenShippingCompany(isGolden);
    setIssueRateValue(isGolden);
    setShippingCompany(shipper);
    if (isGolden) {
      const bennerExchangeContract = await fetchErpExchange();
      let message = '';
      if (!bennerExchangeContract) {
        message = 'Não foi possível obter a cotação do sistema Benner';
      } else {
        message = `Dólar de venda atualizado com valor do sistema Benner: R$ ${bennerExchangeContract.toLocaleString(
          'pt-BR',
        )}`;
      }

      setExchangeContract(bennerExchangeContract);
      snackbar.show(<Text>{message}</Text>, { type: 'warning' });
    } else {
      setExchangeContract(null);
    }
  }

  function onChangeNegotiatorObservation(value) {
    setNegotiatorObservation(value);
  }

  async function init(props) {
    const {
      currency,
      cargoValue,
      exchangeDay,
      freightExchange,
      loadValueType,
      negotiationType,
      rateResponse,
      exchangeContract: loadExchangeContract,
      customData,
      attended,
    } = props;

    const currentCurrency = currency?.value;

    setDefaultData(props);
    setShowDailyRate(false);
    setIsAttended(attended);

    const isGolden = isGoldenShippingCompany(customData?.shipper);
    setGoldenShippingCompany(isGolden);
    setShippingCompany(customData?.shipper);
    setExchangeContract(loadExchangeContract);

    setBalance({
      value: convertValueToCurrency(
        customData?.balance?.value || 0,
        freightExchange,
        currentCurrency,
        'BRL',
      ),
      dollarValue: convertValueToCurrency(
        customData?.balance?.value || 0,
        freightExchange,
        currentCurrency,
        'USD',
      ),
    });

    setTravelMargin(customData?.travelMargin !== undefined ? customData?.travelMargin : 100);

    setData({
      loadValueType,
      negotiationType,
      currency,
      cargoValue,
      exchangeDay,
      freightExchange,
      rateResponse,
    });

    const freightActionFields = await fetchFreightFieldsOptions();
    if (!freightActionFields) {
      return;
    }

    setCustomerFields({
      takerValue: {
        value: convertValueToCurrency(
          customData?.customer?.takerValue?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.takerValue?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
      },
      tollValue: {
        value: convertValueToCurrency(
          customData?.customer?.tollValue?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.tollValue?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        action: setInputAction(
          freightActionFields,
          'toll',
          'not_selected',
          customData?.customer?.tollValue?.action?.id,
        ),
      },
      dischargeValue: {
        value: convertValueToCurrency(
          customData?.customer?.dischargeValue?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.dischargeValue?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        action: setInputAction(
          freightActionFields,
          'discharge',
          'not_selected_discharge',
          customData?.customer?.dischargeValue?.action?.id,
        ),
      },
      gris: {
        value: convertValueToCurrency(
          customData?.customer?.gris?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.gris?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        percentage:
          customData?.customer?.gris?.percentage !== undefined
            ? customData?.customer?.gris?.percentage
            : getPercentageFromValue(customData?.customer?.gris?.value, cargoValue),
        action: setInputAction(
          freightActionFields,
          'griss_ad_valorem',
          'not_selected_griss',
          customData?.customer?.gris?.action?.id,
        ),
      },
      adValorem: {
        value: convertValueToCurrency(
          customData?.customer?.adValorem?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.adValorem?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        percentage:
          customData?.customer?.adValorem?.percentage !== undefined
            ? customData?.customer?.adValorem?.percentage
            : getPercentageFromValue(
                customData?.customer?.adValorem?.value,
                cargoValue,
                AD_VALOREM_PERCENTAGE_PRECISION,
              ),
      },
      chargeValue: {
        value: convertValueToCurrency(
          customData?.customer?.chargeValue?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.chargeValue?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        action: setInputAction(
          freightActionFields,
          'loading_fee',
          'not_selected_fee',
          customData?.customer?.chargeValue?.action?.id,
        ),
      },
      ferry: {
        value: convertValueToCurrency(
          customData?.customer?.ferry?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.ferry?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        action: setInputAction(
          freightActionFields,
          'ferry',
          'not_selected_ferry',
          customData?.customer?.ferry?.action?.id,
        ),
      },
      ICMS: {
        value: convertValueToCurrency(
          customData?.customer?.ICMS?.value || 0,
          freightExchange,
          currentCurrency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customData?.customer?.ICMS?.value || 0,
          freightExchange,
          currentCurrency,
          'USD',
        ),
        percentage:
          customData?.customer?.ICMS?.percentage !== undefined
            ? customData?.customer?.ICMS?.percentage
            : 0,
        action: setInputAction(
          freightActionFields,
          'icms',
          'not_selected_icms',
          customData?.customer?.ICMS?.action?.id,
        ),
      },
    });

    const advanceMoneyValue = percentageToValue(
      Number(customData?.advanceMoney?.percentage || 0),
      Number(customData?.balance?.value || 0),
    );

    setAdvanceMoney({
      value: convertValueToCurrency(advanceMoneyValue, freightExchange, currentCurrency, 'BRL'),
      dollarValue: convertValueToCurrency(
        advanceMoneyValue,
        freightExchange,
        currentCurrency,
        'USD',
      ),
      percentage:
        customData?.advanceMoney?.percentage !== undefined
          ? customData?.advanceMoney?.percentage
          : 0,
    });

    const margin = customData?.advanceMoneyExternal?.percentage || 0;
    const value = percentageToValue(margin, customData?.balance?.value);
    setAdvanceMoneyExternal({
      value: convertValueToCurrency(value, freightExchange, currentCurrency, 'BRL'),
      dollarValue: convertValueToCurrency(value, freightExchange, currentCurrency, 'USD'),
      percentage: margin,
    });

    setCustomerExtraFields(
      customData?.customer?.extras?.map((field) => ({
        id: field.id,
        label: field.label,
        value: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'BRL'),
        dollarValue: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'USD'),
        showToDriver: field.showToDriver,
      })) || [],
    );

    setInsuranceCoopercargaValue({
      value: convertValueToCurrency(
        customData?.discounts?.coopercargaInsurance?.value || 0,
        freightExchange,
        currentCurrency,
        'BRL',
      ),
      dollarValue: convertValueToCurrency(
        customData?.discounts?.coopercargaInsurance?.value || 0,
        freightExchange,
        currentCurrency,
        'USD',
      ),
      percentage:
        customData?.discounts?.coopercargaInsurance?.percentage ||
        getPercentageFromValue(customData?.discounts?.coopercargaInsurance?.value, cargoValue),
    });

    setInputsExtraFields(
      customData?.discounts?.extras?.map((field) => ({
        id: field.id,
        label: field.label,
        value: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'BRL'),
        dollarValue: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'USD'),
        showToDriver: field.showToDriver || false,
      })) || [],
    );

    const issueRateValue =
      customData?.shipmentDiscounts?.issueRate?.value !== undefined
        ? customData?.shipmentDiscounts?.issueRate?.value
        : SERVICE_LEVEL_VALUES.default;

    setShipmentDiscounts((old) => ({
      ...old,
      issueRate: {
        value: convertValueToCurrency(issueRateValue, freightExchange, currentCurrency, 'BRL'),
        dollarValue: convertValueToCurrency(
          issueRateValue,
          freightExchange,
          currentCurrency,
          'USD',
        ),
      },
    }));

    setShipmentDiscountsExtraFields(
      customData?.shipmentDiscounts?.extras?.map((field) => ({
        id: field.id,
        label: field.label,
        value: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'BRL'),
        dollarValue: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'USD'),
        showToDriver: field.showToDriver || false,
      })) || [],
    );

    setShipmentTransferExtraFields(
      customData?.shipmentTransfers?.extras?.map((field) => ({
        id: field.id,
        label: field.label,
        value: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'BRL'),
        dollarValue: convertValueToCurrency(field.value, freightExchange, currentCurrency, 'USD'),
        showToDriver: field.showToDriver || false,
      })) || [],
    );

    setPcpObservation(customData?.shipmentTransfers?.pcpObservation || '');
    setNegotiatorObservation(customData?.shipmentTransfers?.negotiatorObservation || '');

    setHasInitiated(true);
  }

  function setCargoValue(value) {
    if (value && value !== data.cargoValue) {
      const grisValue = ((Number(customerFields?.gris?.percentage) / 100) * value).toFixed(
        BRL_PRECISION,
      );

      const adValoremValue = (
        (Number(customerFields?.adValorem?.percentage) / 100) *
        value
      ).toFixed(BRL_PRECISION);

      const coopercargaInsuranceValue = (
        (Number(insuranceCoopercargaValue?.percentage) / 100) *
        value
      ).toFixed(BRL_PRECISION);

      let totalCustomerValueCalc =
        totalCustomerValue?.total - Number(customerFields?.ICMS?.value || 0);
      totalCustomerValueCalc -= Number(customerFields?.gris?.value || 0);
      totalCustomerValueCalc -= Number(customerFields?.adValorem?.value || 0);

      totalCustomerValueCalc += Number(adValoremValue || 0);
      totalCustomerValueCalc += Number(grisValue || 0);

      const icmsValue =
        totalCustomerValueCalc / (1 - customerFields?.ICMS?.percentage / 100) -
        totalCustomerValueCalc;

      setInsuranceCoopercargaValue({
        value: coopercargaInsuranceValue,
        dollarValue: convertToDollarValue(coopercargaInsuranceValue, dollarToUse, USD_PRECISION),
        percentage: insuranceCoopercargaValue.percentage,
      });

      setCustomerFields((old) => ({
        ...old,
        gris: {
          ...old.gris,
          value: grisValue,
          dollarValue: convertToDollarValue(grisValue, dollarToUse, USD_PRECISION),
        },
        adValorem: {
          ...old.adValorem,
          value: adValoremValue,
          dollarValue: convertToDollarValue(adValoremValue, dollarToUse, USD_PRECISION),
        },
        ICMS: {
          ...old.ICMS,
          value: icmsValue,
          dollarValue: convertToDollarValue(icmsValue, dollarToUse, USD_PRECISION),
          percentage: customerFields?.ICMS?.percentage,
        },
      }));

      setData((old) => ({
        ...old,
        cargoValue: value,
      }));
    }
  }

  const shouldUseToll = useMemo(() => {
    return (
      customerFields?.tollValue?.value > 0 &&
      inputsLoadConditionsToShow.includes(customerFields?.tollValue?.action?.code)
    );
  }, [customerFields?.tollValue?.action?.code, customerFields?.tollValue?.value]);

  const shouldUseDischarge = useMemo(() => {
    return (
      customerFields?.dischargeValue?.value > 0 &&
      inputsLoadConditionsToShow.includes(customerFields?.dischargeValue?.action?.code)
    );
  }, [customerFields?.dischargeValue?.action?.code, customerFields?.dischargeValue?.value]);

  const shouldUseGrisAd = useMemo(() => {
    return (
      customerFields?.gris?.value > 0 &&
      inputsLoadConditionsToShow.includes(customerFields?.gris?.action?.code)
    );
  }, [customerFields?.gris?.action?.code, customerFields?.gris?.value]);

  const shouldUseCharge = useMemo(() => {
    return (
      customerFields?.chargeValue?.value > 0 &&
      inputsLoadConditionsToShow.includes(customerFields?.chargeValue?.action?.code)
    );
  }, [customerFields?.chargeValue?.action?.code, customerFields?.chargeValue?.value]);

  const shouldUseFerry = useMemo(() => {
    return (
      inputsLoadConditionsToShow?.includes(customerFields?.ferry?.action?.code) &&
      customerFields?.ferry?.value > 0
    );
  }, [customerFields?.ferry?.action?.code, customerFields?.ferry?.value]);

  const shouldUseICMS = useMemo(() => {
    return (
      inputsLoadConditionsToShow?.includes(customerFields?.ICMS?.action?.code) &&
      customerFields?.ICMS?.value > 0
    );
  }, [customerFields?.ICMS?.action?.code, customerFields?.ICMS?.value]);

  function getTotalInputValues() {
    let total = 0;
    let totalDollarValue = 0;

    if (insuranceCoopercargaValue?.value) {
      total += Number(insuranceCoopercargaValue?.value);
      totalDollarValue += Number(insuranceCoopercargaValue?.dollarValue);
    }

    if (shouldUseToll) {
      total += Number(customerFields?.tollValue?.value);
      totalDollarValue += Number(customerFields?.tollValue?.dollarValue);
    }

    if (shouldUseDischarge) {
      total += Number(customerFields?.dischargeValue?.value);
      totalDollarValue += Number(customerFields?.dischargeValue?.dollarValue);
    }

    if (shouldUseGrisAd) {
      total += Number(customerFields?.gris?.value);
      totalDollarValue += Number(customerFields?.gris?.dollarValue);
    }

    if (shouldUseGrisAd) {
      total += Number(customerFields?.adValorem?.value);
      totalDollarValue += Number(customerFields?.adValorem?.dollarValue);
      if (goldenShippingCompany) {
        total -= Number(customerFields?.adValorem?.value);
        totalDollarValue -= Number(customerFields?.adValorem?.dollarValue);
      }
    }

    if (shouldUseCharge) {
      total += Number(customerFields?.chargeValue?.value);
      totalDollarValue += Number(customerFields?.chargeValue?.dollarValue);
    }

    if (shouldUseFerry) {
      total += Number(customerFields?.ferry?.value);
      totalDollarValue += Number(customerFields?.ferry?.dollarValue);
    }

    if (shouldUseICMS) {
      total += Number(customerFields?.ICMS?.value);
      totalDollarValue += Number(customerFields?.ICMS?.dollarValue);
    }

    if (inputsExtraFields.length > 0) {
      for (const field of inputsExtraFields) {
        total += Number(field.value);
        totalDollarValue += Number(field.dollarValue);
      }
    }

    return {
      total,
      totalDollarValue,
    };
  }

  const totalInputValuesComputed = useMemo(() => {
    return getTotalInputValues();
  }, [
    insuranceCoopercargaValue,
    inputsExtraFields,
    customerFields,
    shouldUseToll,
    shouldUseDischarge,
    shouldUseGrisAd,
    shouldUseCharge,
    shouldUseFerry,
    shouldUseICMS,
    goldenShippingCompany,
  ]);

  function getFareCompany() {
    const total = totalCustomerValue.total - totalInputValuesComputed.total;
    setFareCompany({
      total: total.toFixed(BRL_PRECISION),
      totalDollarValue: convertToDollarValue(total, erpExchangeValue, USD_PRECISION),
    });
  }

  useEffect(() => {
    getFareCompany();
  }, [totalInputValuesComputed, totalCustomerValue, erpExchangeValue]);

  const totalShipmentDiscounts = useMemo(() => {
    let total = 0;
    let totalDollarValue = 0;
    if (shipmentDiscountsExtraFields?.length) {
      for (const extraField of shipmentDiscountsExtraFields) {
        total += Number(extraField.value);
        totalDollarValue += Number(extraField.dollarValue);
      }
    }
    if (shipmentDiscounts?.issueRate?.value) {
      total += Number(shipmentDiscounts?.issueRate?.value);
      totalDollarValue += Number(shipmentDiscounts?.issueRate?.dollarValue);
    }

    return {
      total,
      totalDollarValue,
    };
  }, [shipmentDiscountsExtraFields, shipmentDiscounts]);

  const shouldUseTransferToll = useMemo(() => {
    return (
      shipmentTransferToShow?.includes(customerFields?.tollValue?.action?.code) &&
      customerFields?.tollValue?.value > 0
    );
  }, [shipmentTransferToShow, customerFields]);

  const shouldUseTransferDischarge = useMemo(() => {
    return (
      shipmentTransferToShow?.includes(customerFields?.dischargeValue?.action?.code) &&
      customerFields?.dischargeValue?.value > 0
    );
  }, [shipmentTransferToShow, customerFields]);

  const shouldUseTransferGris = useMemo(() => {
    return (
      shipmentTransferToShow?.includes(customerFields?.gris?.action?.code) &&
      customerFields?.gris?.value > 0
    );
  }, [shipmentTransferToShow, customerFields]);

  const shouldUseTransferAd = useMemo(() => {
    return (
      shipmentTransferToShow?.includes(customerFields?.adValorem?.action?.code) &&
      customerFields?.adValorem?.value > 0
    );
  }, [shipmentTransferToShow, customerFields]);

  const shouldUseTransferCharge = useMemo(() => {
    return (
      shipmentTransferToShow?.includes(customerFields?.chargeValue?.action?.code) &&
      customerFields?.chargeValue?.value > 0
    );
  }, [shipmentTransferToShow, customerFields]);

  const shouldUseTransferFerry = useMemo(() => {
    return (
      shipmentTransferToShow?.includes(customerFields?.ferry?.action?.code) &&
      customerFields?.ferry?.value > 0
    );
  }, [shipmentTransferToShow, customerFields]);

  const transferTollValue = useMemo(() => {
    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';
    if (shouldUseTransferToll) {
      return {
        value: convertValueToCurrency(
          customerFields?.tollValue?.[prop],
          erpExchangeValue,
          currency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customerFields?.tollValue?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      };
    }

    return {
      value: 0,
      dollarValue: 0,
    };
  }, [shouldUseTransferToll, customerFields, erpExchangeValue, isBRLCurrency]);

  const transferDischargeValue = useMemo(() => {
    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';
    if (shouldUseTransferDischarge) {
      return {
        value: convertValueToCurrency(
          customerFields?.dischargeValue?.[prop],
          erpExchangeValue,
          currency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customerFields?.dischargeValue?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      };
    }

    return {
      value: 0,
      dollarValue: 0,
    };
  }, [shouldUseTransferDischarge, customerFields, erpExchangeValue, isBRLCurrency]);

  const transferGrisValue = useMemo(() => {
    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';
    if (shouldUseTransferGris) {
      return {
        value: convertValueToCurrency(
          customerFields?.gris?.[prop],
          erpExchangeValue,
          currency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customerFields?.gris?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      };
    }

    return {
      value: 0,
      dollarValue: 0,
    };
  }, [shouldUseTransferGris, customerFields, erpExchangeValue, isBRLCurrency]);

  const transferAdValorem = useMemo(() => {
    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';
    if (shouldUseTransferAd) {
      return {
        value: convertValueToCurrency(
          customerFields?.adValorem?.[prop],
          erpExchangeValue,
          currency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customerFields?.adValorem?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      };
    }

    return {
      value: 0,
      dollarValue: 0,
    };
  }, [shouldUseTransferAd, customerFields, erpExchangeValue, isBRLCurrency]);

  const transferChargeValue = useMemo(() => {
    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';
    if (shouldUseTransferCharge) {
      return {
        value: convertValueToCurrency(
          customerFields?.chargeValue?.[prop],
          erpExchangeValue,
          currency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customerFields?.chargeValue?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      };
    }

    return {
      value: 0,
      dollarValue: 0,
    };
  }, [shouldUseTransferCharge, customerFields, erpExchangeValue, isBRLCurrency]);

  const transferFerryValue = useMemo(() => {
    const prop = isBRLCurrency ? 'value' : 'dollarValue';
    const currency = isBRLCurrency ? 'BRL' : 'USD';
    if (shouldUseTransferFerry) {
      return {
        value: convertValueToCurrency(
          customerFields?.ferry?.[prop],
          erpExchangeValue,
          currency,
          'BRL',
        ),
        dollarValue: convertValueToCurrency(
          customerFields?.ferry?.[prop],
          erpExchangeValue,
          currency,
          'USD',
        ),
      };
    }

    return {
      value: 0,
      dollarValue: 0,
    };
  }, [shouldUseTransferFerry, customerFields, erpExchangeValue, isBRLCurrency]);

  const totalShipmentTransfer = useMemo(() => {
    let total = 0;
    let totalDollarValue = 0;
    if (shipmentTransferExtraFields?.length) {
      for (const extraField of shipmentTransferExtraFields) {
        total += Number(extraField.value);
        totalDollarValue += Number(extraField.dollarValue);
      }
    }
    if (customerFields?.tollValue?.action?.code !== 'client_provides_card') {
      total += Number(transferTollValue?.value);
      totalDollarValue += Number(transferTollValue?.dollarValue);
    }

    if (shouldUseTransferDischarge) {
      total += Number(transferDischargeValue?.value);
      totalDollarValue += Number(transferDischargeValue?.dollarValue);
    }

    if (shouldUseTransferGris) {
      total += Number(transferGrisValue?.value);
      totalDollarValue += Number(transferGrisValue?.dollarValue);
    }

    if (shouldUseTransferAd) {
      total += Number(transferAdValorem?.value);
      totalDollarValue += Number(transferAdValorem?.dollarValue);
    }

    if (shouldUseTransferCharge) {
      total += Number(transferChargeValue?.value);
      totalDollarValue += Number(transferChargeValue?.dollarValue);
    }

    if (shouldUseTransferFerry) {
      total += Number(transferFerryValue?.value);
      totalDollarValue += Number(transferFerryValue?.dollarValue);
    }

    return {
      total,
      totalDollarValue,
    };
  }, [
    customerFields,
    transferTollValue,
    transferDischargeValue,
    transferGrisValue,
    transferAdValorem,
    transferChargeValue,
    transferFerryValue,
    shipmentTransferExtraFields,
    shouldUseTransferToll,
    shouldUseTransferDischarge,
    shouldUseTransferGris,
    shouldUseTransferAd,
    shouldUseTransferCharge,
    shouldUseTransferFerry,
  ]);

  function onChangePcpObservation(value) {
    setPcpObservation(value);
  }

  async function getValues({ currency, validateAttended = false } = {}) {
    setErrors({});

    try {
      await schema.validate(
        {
          takerValue: customerFields?.takerValue?.value,
          loadValueType: data?.loadValueType,
          negotiationType: data?.negotiationType,
          balance: balance?.value,
        },
        {
          abortEarly: false,
          context: {
            isAttended: validateAttended,
          },
        },
      );

      if (validateAttended) {
        if (Number(advanceMoney?.value) > Number(advanceMoneyExternal?.value)) {
          setErrors({
            advanceMoney:
              '% de adiantamento negociado deve ser menor que o % máxima de adiantamento no ERP',
          });
          return null;
        }
      }

      const normalized = normalizaFreightValues(
        {
          data,
          customerFields,
          insuranceCoopercargaValue,
          customerExtraFields,
          inputsExtraFields,
          shipmentDiscounts,
          shipmentDiscountsExtraFields,
          shipmentTransferExtraFields,
          pcpObservation,
          negotiatorObservation,
          totalCustomerValue,
          totalInputValuesComputed,
          fareCompany,
          balance,
          advanceMoneyExternal,
          advanceMoney,
          travelMargin,
          totalShipmentDiscounts,
          totalShipmentTransfer,
          erpExchangeValue,
          margins,
        },
        currency || data?.currency?.value,
      );
      return normalized;
    } catch (err) {
      const errorList = {};
      if (Array.isArray(err?.inner)) {
        err.inner.forEach((error) => {
          errorList[error.path] = error.message;
        });
      } else if (err?.path) {
        errorList[err.path] = err?.message || 'Erro de validação';
      } else if (err?.message) {
        errorList._ = err.message;
      }
      setErrors(errorList);
      return null;
    }
  }

  const receiveClientTags = useMemo(() => {
    const tags = [];
    if (Number(customerFields.takerValue?.value) > 0) {
      tags.push('Frete base');
    }
    if (
      Number(customerFields?.tollValue?.value) > 0 &&
      customerFields?.action?.code !== 'client_provides_card'
    ) {
      tags.push('Pedágio');
    }
    if (Number(customerFields?.dischargeValue?.value) > 0) {
      tags.push('Descarga');
    }
    if (Number(customerFields?.chargeValue?.value) > 0) {
      tags.push('Taxa de Carregamento');
    }
    if (Number(customerFields?.gris?.value) > 0) {
      tags.push('Gris');
    }
    if (Number(customerFields?.adValorem?.value) > 0) {
      tags.push('Ad Valorem');
    }
    if (Number(customerFields?.ferry?.value) > 0) {
      tags.push('Balsa');
    }
    if (Number(customerFields?.ICMS?.value) > 0) {
      tags.push('ICMS');
    }
    if (customerExtraFields?.length > 0) {
      tags.push('Valores extras');
    }
    return tags;
  }, [customerFields, customerExtraFields]);

  const inputsTag = useMemo(() => {
    const tags = [];
    if (Number(insuranceCoopercargaValue?.value) > 0) {
      tags.push('Seguro Coopercarga');
    }
    if (shouldUseToll) {
      tags.push('Pedágio');
    }
    if (shouldUseDischarge) {
      tags.push('Descarga');
    }
    if (shouldUseCharge) {
      tags.push('Taxa de Carregamento');
    }
    if (shouldUseGrisAd) {
      tags.push('Gris');
    }
    if (shouldUseGrisAd) {
      tags.push('Ad Valorem');
    }
    if (shouldUseFerry) {
      tags.push('Balsa');
    }
    if (shouldUseICMS) {
      tags.push('ICMS');
    }
    if (inputsExtraFields.length > 0) {
      tags.push('Valores Extra');
    }
    return tags;
  }, [
    insuranceCoopercargaValue,
    shouldUseToll,
    shouldUseDischarge,
    shouldUseGrisAd,
    shouldUseCharge,
    shouldUseFerry,
    shouldUseICMS,
    inputsExtraFields,
  ]);

  const discountsTags = useMemo(() => {
    const tags = [];
    if (shipmentDiscounts.issueRate?.value > 0) {
      tags.push('Taxa de emissão');
    }

    if (shipmentDiscountsExtraFields?.length > 0) {
      tags.push('Valores extras');
    }
    return tags;
  }, [shipmentDiscounts, shipmentDiscountsExtraFields]);

  const transferTags = useMemo(() => {
    const tags = [];

    if (shouldUseTransferToll && customerFields?.tollValue?.code !== 'client_provides_card') {
      tags.push('Pedágio');
    }
    if (shouldUseTransferDischarge) {
      tags.push('Descarga');
    }
    if (shouldUseTransferCharge) {
      tags.push('Taxa de Carregamento');
    }
    if (shouldUseTransferGris) {
      tags.push('Gris');
    }
    if (shouldUseTransferAd) {
      tags.push('Ad Valorem');
    }
    if (shouldUseTransferFerry) {
      tags.push('Balsa');
    }
    if (shipmentTransferExtraFields?.length > 0) {
      tags.push('Valores extras');
    }
    return tags;
  }, [
    shipmentTransferExtraFields,
    customerFields?.tollValue,
    shouldUseTransferToll,
    shouldUseTransferDischarge,
    shouldUseTransferGris,
    shouldUseTransferCharge,
    shouldUseTransferFerry,
    shouldUseTransferAd,
  ]);

  function resetData() {
    if (defaultData) {
      init(defaultData);
    }
  }

  useEffect(() => {
    const margin = margins?.advance_money_percentage_external || 0;
    const balanceValue = isBRLCurrency ? balance?.value : balance?.dollarValue;
    const value = percentageToValue(margin, balanceValue);
    setAdvanceMoneyExternal({
      value: convertValueToCurrency(value, erpExchangeValue, data?.currency?.value, 'BRL'),
      dollarValue: convertValueToCurrency(value, erpExchangeValue, data?.currency?.value, 'USD'),
      percentage: margin,
    });
  }, [
    isBRLCurrency,
    margins?.advance_money_percentage_external,
    balance?.value,
    erpExchangeValue,
    data?.currentCurrency,
    shippingCompany,
  ]);

  const realKm = useMemo(() => {
    if (!totalKm || !customerFields?.takerValue?.value) {
      return {
        value: 0,
        dollarValue: 0,
      }
    }

    const takerValuePerKm = Number(customerFields?.takerValue?.value) / Number(totalKm);

    return {
      value: takerValuePerKm.toFixed(BRL_PRECISION),
      dollarValue: convertToDollarValue(
        takerValuePerKm,
        dollarToUse,
        USD_PRECISION
      ),
    };
  }, [totalKm, customerFields?.takerValue?.value, dollarToUse]);


  return (
    <Context.Provider
      value={{
        init,
        data,
        setData,
        showDailyRate,
        onChangeCurrency,
        onChangeDailyRate,
        isBRLCurrency,
        dollarToUse,
        erpExchangeValue,
        customerFields,
        setCustomerFields,
        receiveClientTags,
        onChangeCustomerValueField,
        onChangeCustomerActionField,
        setCustomerExtraFields,
        customerExtraFields,
        totalCustomerValue,
        insuranceCoopercargaValue,
        onChangeCoopercargaInsuranceValue,
        setInsuranceCoopercargaValue,
        setInputsExtraFields,
        inputsTag,
        inputsExtraFields,
        freightFieldsOptions,
        setCargoValue,
        realKm, 
        setTotalKm,
        totalInputValuesComputed,
        shouldUseToll,
        shouldUseDischarge,
        shouldUseGrisAd,
        shouldUseCharge,
        shouldUseFerry,
        shouldUseICMS,
        fareCompany,
        totalShipmentDiscounts,
        shipmentDiscountsExtraFields,
        shipmentDiscounts,
        transferTags,
        shouldUseTransferToll,
        shouldUseTransferDischarge,
        shouldUseTransferGris,
        shouldUseTransferCharge,
        shouldUseTransferFerry,
        shouldUseTransferAd,
        transferTollValue,
        transferDischargeValue,
        transferChargeValue,
        transferFerryValue,
        totalShipmentTransfer,
        discountsTags,
        setShipmentDiscountsExtraFields,
        shipmentTransferExtraFields,
        setShipmentTransferExtraFields,
        pcpObservation,
        onChangePcpObservation,
        errors,
        getValues,
        goldenShippingCompany,
        isAttended,
        advanceMoneyExternal,
        balance,
        margins,
        travelMargin,
        setTravelMargin,
        advanceMoney,
        defaultData,
        resetData,
        onChangeAdvanceMoney,
        setAdvanceMoney,
        onChangeBalance,
        setBalance,
        onChangeIssueRate,
        onChangeShippingCompany,
        negotiatorObservation,
        onChangeNegotiatorObservation,
        setInsuranceValueBenner,
        convertValueToCurrency,
      }}
    >
      {children}
    </Context.Provider>
  );
}
